import React, { useState, useEffect, useRef } from 'react';
import { Trip } from '../types';

interface EditTripModalProps {
  isOpen: boolean;
  onClose: () => void;
  trip: Trip | null;
  onSave: (tripData: {
    destination: string;
    startDate: string;
    endDate: string;
    travelers: number;
    budget: number;
    interests: string[];
    coverImage?: string;
  }) => Promise<void>;
  onDelete: () => Promise<void>;
  isSaving?: boolean;
  isDeleting?: boolean;
}

const EditTripModal: React.FC<EditTripModalProps> = ({
  isOpen,
  onClose,
  trip,
  onSave,
  onDelete,
  isSaving = false,
  isDeleting = false
}) => {
  const [destination, setDestination] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [travelers, setTravelers] = useState(1);
  const [budget, setBudget] = useState(0);
  const [interests, setInterests] = useState<string[]>([]);
  const [interestInput, setInterestInput] = useState('');
  const [coverImage, setCoverImage] = useState('');
  const scrollPositionRef = useRef<number>(0);

  // Reset form when modal opens/closes or trip changes
  useEffect(() => {
    if (isOpen && trip) {
      setDestination(trip.destination);
      setStartDate(trip.startDate);
      setEndDate(trip.endDate);
      setTravelers(trip.travelers);
      setBudget(trip.budget);
      setInterests(trip.interests || []);
      setCoverImage(trip.coverImage || '');
      setInterestInput('');
    } else if (!isOpen) {
      setDestination('');
      setStartDate('');
      setEndDate('');
      setTravelers(1);
      setBudget(0);
      setInterests([]);
      setCoverImage('');
      setInterestInput('');
    }
  }, [isOpen, trip]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      // Save current scroll position
      scrollPositionRef.current = window.scrollY;
      // Prevent body scroll immediately
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollPositionRef.current}px`;
      document.body.style.width = '100%';
      
      return () => {
        // Cleanup: restore scroll position
        document.body.style.overflow = '';
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        window.scrollTo(0, scrollPositionRef.current);
      };
    } else {
      // Restore scroll position when closing
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      window.scrollTo(0, scrollPositionRef.current);
    }
  }, [isOpen]);

  const handleAddInterest = () => {
    if (interestInput.trim() && !interests.includes(interestInput.trim())) {
      setInterests([...interests, interestInput.trim()]);
      setInterestInput('');
    }
  };

  const handleRemoveInterest = (interestToRemove: string) => {
    setInterests(interests.filter(interest => interest !== interestToRemove));
  };

  const handleSave = async () => {
    if (!destination.trim() || !startDate || !endDate || travelers < 1) {
      alert('Please fill in all required fields');
      return;
    }

    if (new Date(startDate) >= new Date(endDate)) {
      alert('End date must be after start date');
      return;
    }

    try {
      await onSave({
        destination: destination.trim(),
        startDate,
        endDate,
        travelers,
        budget,
        interests,
        coverImage: coverImage.trim() || undefined
      });
      onClose();
    } catch (error) {
      console.error('Error saving trip:', error);
      alert(`Failed to save trip: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this entire trip? This action cannot be undone and will delete all days and activities.')) {
      return;
    }

    try {
      await onDelete();
      onClose();
    } catch (error) {
      console.error('Error deleting trip:', error);
      alert(`Failed to delete trip: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  if (!isOpen || !trip) return null;

  return (
    <div 
      className="modal-overlay"
      onWheel={(e) => {
        // Prevent scroll on overlay, but allow it within modal content
        const target = e.target as HTMLElement;
        const modalContent = target.closest('.modal-content');
        // If the event is on the overlay itself (not inside modal content), prevent it
        if (e.currentTarget === e.target || !modalContent) {
          e.preventDefault();
          e.stopPropagation();
        }
      }}
      style={{ overflow: 'hidden' }}
    >
      <div className="modal-content max-w-2xl max-h-[90vh] flex flex-col">
        {/* Header - styled like EditDayModal */}
        <div className="flex-shrink-0 px-6 py-4 border-b border-[#E3E8E5] flex items-center justify-between">
          <h2 className="text-2xl font-bold text-[#0B1412]">
            Edit Trip: {trip.destination}
          </h2>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-[#F2FBF9] transition-colors"
            disabled={isSaving || isDeleting}
            aria-label="Close"
          >
            <svg className="w-5 h-5 text-[#6F7974]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto scrollbar-thin px-6 py-4">
          <div className="space-y-6">
            {/* Destination */}
            <div className="input-group">
              <label className="input-label">
                Destination *
              </label>
              <input
                type="text"
                value={destination}
                onChange={(e) => setDestination(e.target.value)}
                className="input-field"
                placeholder="Where are you going?"
                disabled={isSaving || isDeleting}
                required
              />
            </div>

            {/* Date Range */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="input-group">
                <label className="input-label">
                  Start Date *
                </label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="input-field"
                  disabled={isSaving || isDeleting}
                  required
                />
              </div>
              <div className="input-group">
                <label className="input-label">
                  End Date *
                </label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="input-field"
                  disabled={isSaving || isDeleting}
                  required
                />
              </div>
            </div>

            {/* Travelers and Budget */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="input-group">
                <label className="input-label">
                  Number of Travelers *
                </label>
                <input
                  type="number"
                  min="1"
                  max="20"
                  value={travelers}
                  onChange={(e) => setTravelers(parseInt(e.target.value) || 1)}
                  className="input-field"
                  disabled={isSaving || isDeleting}
                  required
                />
              </div>
              <div className="input-group">
                <label className="input-label">
                  Budget (USD)
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={budget ? budget / 100 : ''}
                  onChange={(e) => setBudget(e.target.value ? Math.round(parseFloat(e.target.value) * 100) : 0)}
                  className="input-field"
                  placeholder="0.00"
                  disabled={isSaving || isDeleting}
                />
                <p className="text-xs text-[#6F7974] mt-1">
                  Budget is stored in cents. Enter amount in dollars.
                </p>
              </div>
            </div>

            {/* Cover Image */}
            <div className="input-group">
              <label className="input-label">
                Cover Image URL (Optional)
              </label>
              <input
                type="url"
                value={coverImage}
                onChange={(e) => setCoverImage(e.target.value)}
                className="input-field"
                placeholder="https://example.com/image.jpg"
                disabled={isSaving || isDeleting}
              />
              <p className="text-xs text-[#6F7974] mt-1">
                URL for the trip cover image
              </p>
              {coverImage && (
                <div className="mt-2">
                  <img 
                    src={coverImage} 
                    alt="Cover preview" 
                    className="w-full h-32 object-cover rounded-lg border border-[#E3E8E5]"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                </div>
              )}
            </div>

            {/* Interests */}
            <div className="input-group">
              <label className="input-label">
                Interests
              </label>
              <div className="flex gap-2 mb-2">
                <input
                  type="text"
                  value={interestInput}
                  onChange={(e) => setInterestInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddInterest())}
                  className="input-field flex-1"
                  placeholder="Add an interest (e.g., culture, food, adventure)"
                  disabled={isSaving || isDeleting}
                />
                <button
                  type="button"
                  onClick={handleAddInterest}
                  disabled={isSaving || isDeleting || !interestInput.trim()}
                  className="btn-secondary whitespace-nowrap"
                >
                  Add
                </button>
              </div>
              {interests.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {interests.map((interest, index) => (
                    <span
                      key={index}
                      className="badge badge-primary"
                    >
                      {interest}
                      <button
                        type="button"
                        onClick={() => handleRemoveInterest(interest)}
                        disabled={isSaving || isDeleting}
                        className="ml-2 text-primary-600 hover:text-primary-800 font-bold disabled:cursor-not-allowed"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer - buttons on separate lines in mobile, one row in desktop */}
        <div className="flex-shrink-0 px-6 py-4 border-t border-[#E3E8E5] flex flex-col md:flex-row gap-3 md:gap-0 md:items-center md:justify-between">
          <button
            onClick={onClose}
            disabled={isSaving || isDeleting}
            className="btn-secondary w-full md:w-auto"
          >
            Cancel
          </button>
          
          <div className="flex flex-col md:flex-row gap-3 md:gap-3 w-full md:w-auto md:ml-auto">
            <button
              onClick={handleDelete}
              disabled={isSaving || isDeleting}
              className="btn-secondary text-red-600 hover:text-red-700 hover:bg-red-50 w-full md:w-auto"
            >
              {isDeleting ? (
                <>
                  <div className="loading-spinner w-4 h-4 mr-2 inline-block"></div>
                  Deleting...
                </>
              ) : (
                '🗑️ Delete Trip'
              )}
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving || isDeleting || !destination.trim() || !startDate || !endDate || travelers < 1}
              className="btn-primary w-full md:w-auto"
            >
              {isSaving ? (
                <>
                  <div className="loading-spinner w-4 h-4 mr-2 inline-block"></div>
                  Saving...
                </>
              ) : (
                '💾 Save Changes'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditTripModal;
